import type { TFunction } from 'i18next';
import type { Outcome } from '../../../../../api/strategy-client/strategy-client.type';
import { formatInitiativeDate } from '../../../strategy.helpers';

/**
 * Get outcome information to display in the side section
 *
 * @param {Outcome} outcome - The outcome to get information for
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {Array<{label: string, value: string | number | null | undefined}>} Label-value pairs with outcome date information
 */
const getOutcomeInfo = (
  outcome: Outcome,
  t: TFunction,
): { label: string; value: string | number | null | undefined }[] => {
  return [
    {
      label: t('strategy.initiative_performance.start_date'),
      value: formatInitiativeDate(outcome.start_date, t),
    },
    {
      label: t('strategy.initiative_performance.activity_start_date'),
      value: formatInitiativeDate(outcome.activity_start_date, t),
    },
    {
      label: t('strategy.initiative_performance.end_date'),
      value: formatInitiativeDate(outcome.end_date, t),
    },
    {
      label: t('strategy.initiative_performance.activity_end_date'),
      value: formatInitiativeDate(outcome.activity_end_date, t),
    },
  ];
};

/**
 * Get the last month task count from the data
 *
 * @param {Record<string, number | null> | undefined} data - The data to get the last month task count from
 * @returns {number} The last month task count
 */
const getLastMonthTaskCount = (data: Record<string, number | null> | undefined): number => {
  if (!data) {
    return 0;
  }

  // Get the last entry (month) that is not null and return its value
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return 0;
  }
  // Sort by month key ascending, assuming keys are YYYY-MM (lexicographically sorted)
  const sorted = entries.sort(([a], [b]) => a.localeCompare(b));
  for (let i = sorted.length - 1; i >= 0; i--) {
    const [, value] = sorted[i];
    if (value !== null && value !== undefined) {
      return value;
    }
  }
  return 0;
};

export { getLastMonthTaskCount, getOutcomeInfo };
